<template>
  <MainLayout>
    <div>
      <!-- MAIN LOADER -->
      <template v-if="loader && !campaign">
          <Loader
            :size="'big'"
          />
      </template> <!-- END MAIN LOADER -->
      <template v-else-if="currentPackage">
        <!-- HEADER -->
        <div class="d-flex justify-content-between align-items-center mb-17 mt-7">
          <div class="col-lg-7">
            <h1 class="text-uppercase mb-0">
              <span>{{ currentPackage.name }}</span>
            </h1>
          </div>
          <div class="d-flex">
            <template v-if="!campaign.is_read_only">
              <div id="edit-package-wrap">
                <router-link
                  :to="{ name: 'packages.edit', params: { cId: campaign.id, id: currentPackage.id } }"
                  class="btn btn-sm btn-color10 me-2"
                  :class="{
                    disabled: isCampaignPaused
                  }"
                  v-if="campaign.status !== 'FINISHED'"
                >
                  {{ $t('edit_group') }}
                </router-link>
              </div>
              <b-tooltip
                target="edit-package-wrap"
                v-if="isCampaignPaused"
              >
                {{ $t('editing_a_campaign_group_is_not_allowed') }}
              </b-tooltip>
              <template v-if="campaign.status !== 'FINISHED'">
                <template v-if="currentPackage.status === 'ACTIVE'">
                  <Button
                    id="pause-package-button"
                    class="btn-sm btn-color1 me-2"
                    @click="openPauseCampaignPackageModal"
                    :softDisabled="isCampaignPaused"
                  >
                    {{ $t('pause') }}
                  </Button>
                  <b-tooltip
                    target="pause-package-button"
                    v-if="isCampaignPaused"
                  >
                    {{ $t('not_allowed_to_pause_a_campaign_group') }}
                  </b-tooltip>
                  <PauseCampaignPackageModal
                    :campaign="campaign"
                    :segmentation="currentPackage"
                    @package-paused="getCampaign"
                  />
                </template>
                <template v-else-if="currentPackage.status === 'PAUSED'">
                  <Button
                    id="activate-package-button"
                    class="btn-sm btn-color1 me-2"
                    @click="openActivateCampaignPackageModal"
                    :softDisabled="isCampaignPaused"
                  >
                    {{ $t('enable') }}
                  </Button>
                  <b-tooltip
                    target="activate-package-button"
                    v-if="isCampaignPaused"
                  >
                    {{ $t('not_allowed_to_pause_a_campaign_group') }}
                  </b-tooltip>
                  <ActivateCampaignPackageModal
                    :campaign="campaign"
                    :segmentation="currentPackage"
                    @package-activated="getCampaign"
                  />
                </template>
              </template>
            </template>
            <router-link
              :to="{ name: 'campaigns.show', params: { id: campaign.id } }"
              class="btn btn-sm btn-color10"
            >
              {{ $t('back') }}
            </router-link>
          </div>
        </div> <!-- END HEADER -->
        <!-- MESSAGES -->
        <div
          class="mb-4"
          v-if="currentPackage.status === 'ERROR' || currentPackage.status === 'PAUSED'"
        >
          <div
            class="error-message-card text-white font-size-small p-3"
            v-if="currentPackage.status === 'ERROR'"
          >
            <p
              class="mb-0"
            >
              {{ $t('error_creating_campaign_group') }}
              <span>
                <RetryCampaignPackageCreationOnPlatformButton
                  :packageId="currentPackage.id"
                  @retry-successful="getCampaign"
                  @retry-error="handleRetryError"
                  color="white"
                />
              </span>
            </p>
          </div>
          <div
            class="error-message-card text-white font-size-small p-3"
            v-if="currentPackage.status === 'PAUSED'"
          >
            <p
              class="mb-0"
            >
              {{ $t('this_campaign_group_is_paused') }}
            </p>
          </div>
        </div> <!-- END MESSAGES -->
        <!-- OVERVIEW -->
        <div class="mb-10">
          <h2 class="text-uppercase font-size-regular mb-4">
            <span>{{ $t('general_data') }}</span>
          </h2>
          <div class="card">
            <div class="card-body">
              <div class="d-flex">
                <div class="col-6">
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('name') }}</strong>
                    <span v-b-tooltip.hover :title="currentPackage.name">{{ currentPackage.name.length >= 22 ? currentPackage.name.slice(0, 22) + '...' : currentPackage.name }}</span>
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('status') }}</strong>
                    {{ currentPackage.status | formatCampaignStatus }}
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('spending_to_date') }}</strong>
                    {{ currentPackage.spending / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('total_impressions') }}</strong>
                    {{ currentPackage.impressions | formatNumber }}
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('total_clicks') }}</strong>
                    {{ currentPackage.clicks | formatNumber }}
                  </div>
                </div>
                <div class="col-6">
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('total_conversions') }}</strong>
                    {{ currentPackage.conversions | formatNumber }}
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('ctr') }}</strong>
                    {{ currentPackage.ctr | formatNumber }}%
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('cpm') }}</strong>
                    {{ currentPackage.cpm / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                  <div class="d-flex border-bottom border-dashed p-2 px-4">
                    <strong class="col-7 text-color10">{{ $t('cpc') }}</strong>
                    {{ currentPackage.cpa / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> <!-- END OVERVIEW -->
        <!-- KEYWORDS -->
        <div>
          <!-- HEADER -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 class="text-uppercase font-size-regular">
                <span>{{ $t('keyword') }}</span>
              </h2>
            </div>
          </div> <!-- END HEADER -->
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-striped align-middle table-row-dashed fs-6 gy-3 dataTable no-footer">
                  <thead class=" fw-600">
                    <tr>
                      <th>{{ $t('keyword') }}</th>
                      <th>{{ $t('spending') }}</th>
                      <th>{{ $t('impressions') }}</th>
                      <th>{{ $t('clicks') }}</th>
                      <th>{{ $t('conversions') }}</th>
                      <th>{{ $t('ctr') }}</th>
                      <th>{{ $t('cpm') }}</th>
                      <th>{{ $t('cpc') }}</th>
                      <th>{{ $t('cpa') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(keyword, index) in keywords"
                      :key="index"
                    >
                      <td class="ps-3">
                        {{ keyword.text }}
                      </td>
                      <td>{{ keyword.spending / 100 | formatMoney(campaign.advertiser.currency) }}</td>
                      <td>{{ keyword.impressions | formatNumber }}</td>
                      <td>{{ keyword.clicks | formatNumber }}</td>
                      <td>{{ keyword.conversions | formatNumber }}</td>
                      <td>{{ keyword.ctr }}%</td>
                      <td>{{ keyword.cpm / 100 | formatMoney(campaign.advertiser.currency) }}</td>
                      <td>{{ keyword.cpc / 100 | formatMoney(campaign.advertiser.currency) }}</td>
                      <td class="pe-3">{{ keyword.cpa / 100 | formatMoney(campaign.advertiser.currency) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div> <!-- END KEYWORDS -->
        <!-- ADS -->
        <div class="mt-10">
          <!-- HEADER -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 class="text-uppercase font-size-regular">
                <span>{{ $t('advertisements') }}</span>
              </h2>
            </div>
          </div> <!-- END HEADER -->
          <CreativeListTable
            :creatives="creatives"
          />
        </div> <!-- END ADS -->
      </template>
    </div>
  </MainLayout>
</template>

<script>

import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import { mapActions, mapState } from 'vuex'
import variables from '@/mixins/variables'

import ActivateCampaignPackageModal from '@/components/campaigns/Modals/ActivateCampaignPackageModal'
import Button from '@/components/common/Button/Button'
import CreativeListTable from '@/components/creatives/CreativeListTable'
import Loader from '@/components/common/Loader/Loader'
import MainLayout from '@/components/layouts/MainLayout'
import PauseCampaignPackageModal from '@/components/campaigns/Modals/PauseCampaignPackageModal'
import RetryCampaignPackageCreationOnPlatformButton from '@/components/campaigns/RetryCampaignPackageCreationOnPlatformButton'

export default {
  mixins: [variables],
  components: {
    ActivateCampaignPackageModal,
    Button,
    CreativeListTable,
    Loader,
    MainLayout,
    PauseCampaignPackageModal,
    RetryCampaignPackageCreationOnPlatformButton
  },
  data () {
    return {
      campaign: null,
      loader: false
    }
  },
  metaInfo () {
    return {
      title: this.currentPackage ? this.currentPackage.name : ''
    }
  },
  created () {
    if (this.storeCampaign) {
      this.campaign = this.storeCampaign
    } else this.getCampaign()
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    ...mapState('campaigns', {
      storeCampaign: state => state.currentCampaign
    }),
    /**
     * Creatives
    */
    creatives () {
      return this.currentPackage.lines[0].creatives
    },
    currentPackage () {
      if (!this.campaign) return null
      return this.campaign.packages.find(item => {
        return item.id === this.$route.params.id
      })
    },
    /**
     * Is campaign paused
     */
    isCampaignPaused () {
      if (!this.campaign) return false
      return this.campaign.status === 'PAUSED'
    },
    /**
     * Keywords
    */
    keywords () {
      return this.currentPackage.lines[0].keywords
    }
  },
  methods: {
    ...mapActions('campaigns', [
      'setCurrentCampaign'
    ]),
    /**
     * Get campaign
     */
    async getCampaign () {
      this.loader = true
      try {
        const campaign = await CampaignsService.getCampaign(this.$route.params.cId)
        this.campaign = campaign
        this.setCurrentCampaign({ campaign: campaign })
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
        this.$router.push('/')
      }
      this.loader = false
    },
    /**
     * Handle retry error
     */
    handleRetryError (error) {
      error.handleGlobally && error.handleGlobally()
    },
    /**
     * Open activate campaign package modal
     */
    openActivateCampaignPackageModal () {
      this.$bvModal.show('activate-campaign-package-modal')
    },
    /**
     * Open pause campaign package modal
     */
    openPauseCampaignPackageModal () {
      this.$bvModal.show('pause-campaign-package-modal')
    },
    /**
     * Open replicate line modal
     */
    openReplicateLineModal (line) {}
  }
}
</script>

<style lang="scss" scoped>
.wrap-text {
  width: 100%;
  word-wrap: break-word;
}
</style>
